import { useMutation, useQueryClient } from "@tanstack/react-query"
import { PERSONALIZED_SUMMARY_QUERY_KEY } from "./usePersonalizedSummary"
import { api } from "@/api"
import { RateLimitError } from "@/components/PersonalizedSummary/hooks/usePersonalizedSummaryGeneration"

export interface PersonalizedSummary {
    id: string
    summary: string
    billId: number
    billTitle: string
    profileId: string
    profileName: string
    createdAt: string
}

const applyPersonalizedSummary = async ({ boardId, billId, profileId, summary }): Promise<PersonalizedSummary> => {
    const response = await api.post(
        "/api/personalization/bill-summaries/",
        {
            summary,
            bill_id: billId,
            ai_tracking_board_id: boardId,
            personalization_profile_id: profileId,
            preview: false,
        },
        { throwOnNotOk: false },
    )

    // Handle rate limit errors
    if (response.status === 429) {
        const errorData = await response.json()
        throw new RateLimitError(
            errorData.error || "Rate limit exceeded. Please try again later.",
            errorData.retry_after_seconds || 300,
        )
    }

    // Handle other errors
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    const data = await response.json()

    return data
}

export const usePersonalizedSummaryApplication = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: applyPersonalizedSummary,
        onSuccess: (data, variables) => {
            // Invalidate the personalization query cache to refresh the data
            queryClient.invalidateQueries({
                queryKey: [PERSONALIZED_SUMMARY_QUERY_KEY, variables.billId, variables.boardId],
            })
        },
    })
}
